/**
 * The signed-in person's unread notifications, live (hooks/useNotifications.ts): the bell hears
 * of a new one the moment the server writes it, and of one read or deleted elsewhere (another
 * tab, the other app), without reloading the page. Before this the bell read its list once per
 * page load and asked for the unread count every 30 seconds.
 *
 * The rules let a person read the notifications addressed to them (firestore.rules,
 * "Notifications"), so the query names them. Two equality filters need no index of their own.
 */
import { getFirestore, collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import { getFirebaseApp } from './storage';
import type { AppNotification } from '../types';

/** At most this many unread notifications are watched; at this many the count is asked of the server */
export const UNREAD_WATCHED = 50;

export interface UnreadChange {
    /** How many are unread; UNREAD_WATCHED means at least that many */
    count: number;
    /** Those written since the watch began (not the ones already there when it began) */
    arrived: AppNotification[];
    /** Ids no longer unread: read or deleted, here or elsewhere */
    gone: string[];
}

/**
 * Watches the person's unread notifications. Null when the cloud is not set up here (the
 * caller asks the server from time to time instead); after `onError` the watch has stopped.
 */
export const watchUnreadNotifications = (
    userId: string,
    onChange: (change: UnreadChange) => void,
    onError: (error: unknown) => void,
): (() => void) | null => {
    const app = getFirebaseApp();
    if (!app) return null;
    const unread = query(
        collection(getFirestore(app), 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false),
        limit(UNREAD_WATCHED),
    );
    let first = true;
    return onSnapshot(
        unread,
        (snapshot) => {
            const changes = first ? [] : snapshot.docChanges();
            first = false;
            onChange({
                count: snapshot.size,
                // The record's id is the notification's id (as the server's list gives it)
                arrived: changes.filter(c => c.type === 'added').map(c => ({ ...(c.doc.data() as AppNotification), id: c.doc.id })),
                gone: changes.filter(c => c.type === 'removed').map(c => c.doc.id),
            });
        },
        (error) => {
            console.warn('[notifications] live updates stopped:', error);
            onError(error);
        },
    );
};
